import React, { useEffect, useMemo, useState } from "react";
import { DataTable } from "@/components/ui/table/data-table";
import { useParams } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { displayValue } from "@/lib/helper";
import Pagination from "@/components/ui/table/pagination-table";
import usePagination from "@/hooks/use-pagination";
import { MembershipFacility } from "@/types/member";
import { Separator } from "@/components/ui/separator";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { memo } from "react";
import { Badge } from "@/components/ui/badge";
import {
  useGetMembershipFacilityQuery,
  useUpdateMembershipFacilityCreditsMutation,
  useAddMemberFacilityMutation,
  useGetMemberByIdQuery,
} from "@/services/memberApi";
import { useGetStaffByIdQuery } from "@/services/staffsApi";
import { useGetFacilityListQuery } from "@/services/facilityApis";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ErrorType } from "@/types";
import { toast } from "@/components/ui/use-toast";
import { CreditType } from "@/types/enums";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { MoreHorizontal, Plus, Minus, History, Loader2, X, Info, CreditCard, Calendar as CalendarIcon, FileText } from "lucide-react";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { Card } from "@/components/ui/card";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useGetCreditLogsQuery } from "@/services/memberApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
const initialValue = {
  limit: 5,
  offset: 0,
  sort_order: "desc",
  sort_key: "facility_id",
};

interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_order: string;
  sort_key: string;
}
const MemberMembershipFacilities = () => {
  const { memberId } = useParams();
  const [searchCriteria, setSearchCriteria] =
    useState<SearchCriteriaType>(initialValue);
  const [query, setQuery] = useState("");

  // Sheet states
  const [adjustmentSheetOpen, setAdjustmentSheetOpen] = useState(false);
  const [historySheetOpen, setHistorySheetOpen] = useState(false);
  const [adjustmentType, setAdjustmentType] = useState<"add" | "subtract">("add");
  const [selectedFacility, setSelectedFacility] = useState<MembershipFacility | null>(null);
  const [adjustmentAmount, setAdjustmentAmount] = useState<number>(0);
  const [adjustmentExpiryDate, setAdjustmentExpiryDate] = useState<Date | undefined>(undefined);
  const [adjustmentExpiryOpen, setAdjustmentExpiryOpen] = useState(false);
  const [notes, setNotes] = useState<string>("");

  const currentRemainingCredits = useMemo(() => {
    if (!selectedFacility) return 0;
    return (selectedFacility as any).remaining_credits ?? selectedFacility.credits ?? 0;
  }, [selectedFacility]);

  const validationError = useMemo(() => {
    if (adjustmentAmount <= 0) return "";
    if (adjustmentType === "add" && adjustmentAmount > 1000) {
      return "Credits cannot exceed 1,000";
    }
    if (adjustmentType === "subtract" && adjustmentAmount > currentRemainingCredits) {
      return `Cannot subtract more than available balance (${currentRemainingCredits})`;
    }
    return "";
  }, [adjustmentAmount, adjustmentType, currentRemainingCredits]);

  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const [
    UpdateCredits,
    { isLoading: updateCreditsLoading },
  ] = useUpdateMembershipFacilityCreditsMutation();

  const [addMemberFacility, { isLoading: addFacilityLoading }] = useAddMemberFacilityMutation();

  // Add Facility Sheet states
  const [addFacilitySheetOpen, setAddFacilitySheetOpen] = useState(false);
  const [newFacilityId, setNewFacilityId] = useState<string>("");
  const [newFacilityCredits, setNewFacilityCredits] = useState<number>(0);
  const [newFacilityExpiryDate, setNewFacilityExpiryDate] = useState<Date | undefined>(undefined);
  const [newFacilityExpiryOpen, setNewFacilityExpiryOpen] = useState(false);
  const [newFacilityNotes, setNewFacilityNotes] = useState<string>("");

  const { data: facilitiesList } = useGetFacilityListQuery(
    { id: orgId, query: "" },
    { skip: !addFacilitySheetOpen }
  );

  useEffect(() => {
    setSearchCriteria((prev) => {
      const newCriteria = { ...prev };
      newCriteria.offset = 0;
      newCriteria.sort_key = "facility_id";
      newCriteria.sort_order = "desc";
      return newCriteria;
    });
  }, [setSearchCriteria]);

  useEffect(() => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(searchCriteria)) {
      console.log({ key, value });
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    }
    const newQuery = params.toString();
    console.log({ newQuery });
    setQuery(newQuery);
  }, [searchCriteria]);

  const {
    data: membershipData,
    isLoading,
    refetch,
    error,
    isError,
  } = useGetMembershipFacilityQuery(
    { member_id: Number(memberId), query: query, org_id: orgId },
    {
      skip: query == "",
    }
  );
  const tableData = useMemo(() => {
    return Array.isArray(membershipData?.data) ? membershipData?.data : [];
  }, [membershipData]);

  const toggleSortOrder = (key: string, order: string) => {
    if (order === "asc" || order === "desc") {
      setSearchCriteria((prev) => ({
        ...prev,
        sort_key: key,
        sort_order: order,
      }));
    } else {
      setSearchCriteria((prev) => ({
        ...prev,
        sort_key: "",
        sort_order: "",
      }));
    }
  };

  const handleOpenAdjustment = (facility: MembershipFacility, type: "add" | "subtract") => {
    setSelectedFacility(facility);
    setAdjustmentType(type);
    setAdjustmentAmount(0);
    setAdjustmentExpiryDate(undefined);
    setNotes("");
    setAdjustmentSheetOpen(true);
  };

  const handleOpenHistory = (facility: MembershipFacility) => {
    setSelectedFacility(facility);
    setHistorySheetOpen(true);
  };

  const handleSaveAdjustment = async () => {
    if (!selectedFacility) return;

    if (adjustmentAmount <= 0) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Credit amount must be greater than 0",
      });
      return;
    }

    if (adjustmentType === "add") {
      const payload = {
        member_id: Number(memberId),
        facilities: [
          {
            facility_id: (selectedFacility as any).facility_id || (selectedFacility as any).id,
            credits: adjustmentAmount,
            credit_type: CreditType.amount,
            expiry_date: adjustmentExpiryDate ? format(adjustmentExpiryDate, "yyyy-MM-dd") : "",
            message: notes,
          },
        ],
      };

      try {
        const resp = await addMemberFacility(payload).unwrap();
        if (resp) {
          refetch();
          setAdjustmentSheetOpen(false);
          toast({
            variant: "success",
            title: "Credits Added Successfully",
          });
          // Reset adjustment states
          setAdjustmentAmount(0);
          setAdjustmentExpiryDate(undefined);
          setNotes("");
        }
      } catch (error) {
        console.error("Error adding facility", error);
        const typedError = error as ErrorType;
        const status = (error as any)?.status;
        const isBadRequest = status === 400;
        toast({
          variant: "destructive",
          title: "Error adding facility",
          description:
            typedError.data?.detail ||
            (isBadRequest
              ? "Please check the entered details — notes may be too long or a field is invalid."
              : "Something went wrong."),
        });
      }
    } else {
      // Subtract logic remains unchanged
      const currentCredits = (selectedFacility as any).remaining_credits ?? selectedFacility.credits ?? 0;
      const newCredits = Math.max(0, currentCredits - adjustmentAmount);

      const payload = {
        member_id: Number(memberId),
        facility_id: (selectedFacility as any).facility_id || (selectedFacility as any).id,
        credits: newCredits,
        message: notes,
      };

      try {
        await handleCreditChanges(payload);
        setAdjustmentSheetOpen(false);
      } catch (error) {
        console.error("Failed to save changes", error);
      }
    }
  };

  const handleSaveAddFacility = async () => {
    if (!newFacilityId || !newFacilityExpiryDate) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please fill all required fields correctly.",
      });
      return;
    }

    if (newFacilityCredits <= 0) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Credit amount must be greater than 0",
      });
      return;
    }

    const payload = {
      member_id: Number(memberId),
      facilities: [
          {
            facility_id: Number(newFacilityId),
            credits: newFacilityCredits,
            credit_type: CreditType.amount,
            expiry_date: format(newFacilityExpiryDate, "yyyy-MM-dd"),
            message: newFacilityNotes,
          },
      ],
    };

    try {
      const resp = await addMemberFacility(payload).unwrap();
      if (resp) {
        refetch();
        setAddFacilitySheetOpen(false);
        toast({
          variant: "success",
          title: "Facility Added Successfully",
        });
        // Reset states
        setNewFacilityId("");
        setNewFacilityCredits(0);
        setNewFacilityExpiryDate(undefined);
        setNewFacilityNotes("");
      }
    } catch (error) {
      console.error("Error adding facility", error);
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error adding facility",
        description: typedError.data?.detail || "Something went wrong.",
      });
    }
  };

  const handleCreditChanges = async (payload: {
    member_id: number;
    facility_id: number;
    credits: number;
    message?: string;
  }) => {
    try {
      const resp = await UpdateCredits(payload).unwrap();
      if (resp) {
        refetch();
        toast({
          variant: "success",
          title: "Credits Updated Successfully",
        });
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in updation",
          description: `${typedError.data?.detail || (typedError.data as { message?: string }).message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in Updation",
          description: `Something Went Wrong.`,
        });
      }
    }
  };
  const columns: ColumnDef<MembershipFacility>[] = [
    {
      accessorKey: "Name",
      meta: "Name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Name"
          sortKey="facility_name"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const status = row.original.status?.toLowerCase();
        const isInactive = status && status !== "active";
        return (
          <div className="flex items-center gap-2 text-ellipsis whitespace-nowrap overflow-hidden">
            <span>{displayValue(row?.original.facility_name)}</span>
            {isInactive && (
              <Badge
                variant="outline"
                className="h-5 rounded-full border-amber-200 bg-amber-50 px-2 py-0 text-[10px] font-semibold text-amber-700"
              >
                Expired
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "credit",
      meta: "credit",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Available Credits"
          sortKey="remaining_credits"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const creditType = row.original.credit_type;
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {creditType === CreditType.unlimited ? (
              <>Unlimited</>
            ) : (
              displayValue((row.original as any).remaining_credits ?? row.original.credits)
            )}
            {((row.original as any).remaining_credits ?? row.original.credits) > 0 ? " Credits" : " Credits"}
          </div>
        );
      },
    },
    {
      accessorKey: "credit_type",
      meta: "credit_type",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Credit Type"
          sortKey="credit_type"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row?.original.credit_type)}
          </div>
        );
      },
    },

    {
      accessorKey: "action",
      meta: "actions",
      header: () => "Action",
      cell: ({ row }) => {
        const creditType = row.original.credit_type;
        const canAdjust = creditType === CreditType.amount;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {canAdjust && (
                <>
                  <DropdownMenuItem onClick={() => handleOpenAdjustment(row.original, "add")}>
                    <Plus className="mr-2 h-4 w-4 text-green-600" />
                    <span>Add Credit</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleOpenAdjustment(row.original, "subtract")}>
                    <Minus className="mr-2 h-4 w-4 text-red-600" />
                    <span>Subtract Credit</span>
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuItem onClick={() => handleOpenHistory(row.original)}>
                <History className="mr-2 h-4 w-4 text-blue-600" />
                <span>Credit History</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const totalRecords = membershipData?.filtered_counts ?? 0;
  useEffect(() => {
    if (isError && navigator.onLine) {
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error",
        description: typedError.data?.detail ?? typedError.data?.message,
      });
    }
  }, [isError]);
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  return (
    <>
      <div className="flex justify-between items-center mb-2">
        <h1 className="font-bold text-xl ">Facilities</h1>
        <Button
          onClick={() => setAddFacilitySheetOpen(true)}
          className="bg-green-600 hover:bg-green-700 text-white rounded-md px-6"
        >
          <Plus className="mr-2 h-4 w-4" />
          Create New
        </Button>
      </div>
      <Separator />
      {/* Data Table */}
      <div className="mt-4 bg-white">
        <DataTable columns={columns} data={tableData} isLoading={isLoading} />{" "}
        {/* //isLoading */}
      </div>

      {/* Pagination */}
      {tableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      {/* Add Facility Sheet */}
      <Sheet open={addFacilitySheetOpen} onOpenChange={setAddFacilitySheetOpen}>
        <SheetContent side="right" className="sm:max-w-[600px] flex flex-col h-full p-0 bg-gray-50" hideCloseButton>
          <SheetHeader className="px-6 py-4 bg-white border-b sticky top-0 z-10 shadow-sm">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <button onClick={() => setAddFacilitySheetOpen(false)} className="hover:bg-gray-100 p-1 rounded-full transition-colors">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
                <SheetTitle className="text-xl font-bold text-gray-900">
                  Add Facility
                </SheetTitle>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setAddFacilitySheetOpen(false)}
                  className="h-9 px-6 rounded-md"
                >
                  Cancel
                </Button>
                <LoadingButton
                  className="h-9 px-8 rounded-md bg-green-600 hover:bg-green-700 text-white font-semibold"
                  loading={addFacilityLoading}
                  disabled={!newFacilityId || newFacilityNotes.length > 200}
                  onClick={handleSaveAddFacility}
                >
                  Save
                </LoadingButton>
              </div>
            </div>
          </SheetHeader>

          <ScrollArea className="flex-1">
            <div className="p-6 space-y-6">
              {/* Basic Information Section */}
              <Card className="p-6 shadow-sm border-l-4 border-l-green-600 bg-white">
                <div className="flex items-center gap-2 mb-6">
                  <div className="p-2 bg-green-50 rounded-lg text-green-600">
                    <Info className="h-4 w-4" />
                  </div>
                  <h3 className="font-medium text-gray-900 text-sm italic">Basic Information</h3>
                </div>

                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-gray-700">Facility <span className="text-red-500">*</span></label>
                    <Select value={newFacilityId} onValueChange={setNewFacilityId}>
                      <SelectTrigger className="h-11 bg-white border-gray-200 focus:ring-1 focus:ring-green-500">
                        <SelectValue placeholder="Select Facility" />
                      </SelectTrigger>
                      <SelectContent>
                        {facilitiesList?.map((f) => (
                          <SelectItem key={f.id} value={f.id}>
                            {f.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </Card>

              {/* Credit & Validity Section */}
              <Card className="p-6 shadow-sm border-l-4 border-l-blue-600 bg-white">
                <div className="flex items-center gap-2 mb-6">
                  <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                    <CreditCard className="h-4 w-4" />
                  </div>
                  <h3 className="font-medium text-gray-900 text-sm italic">Credit & Validity Configuration</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-gray-700">Credits <span className="text-red-500">*</span></label>
                    <Input
                      type="number"
                      value={newFacilityCredits || ""}
                      onChange={(e) => setNewFacilityCredits(Math.max(0, parseInt(e.target.value) || 0))}
                      className="h-11 bg-white border-gray-200 focus-visible:ring-1 focus-visible:ring-green-500"
                      placeholder="0"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-gray-700">
                      Expiry Date <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <Popover open={newFacilityExpiryOpen} onOpenChange={setNewFacilityExpiryOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "h-11 w-full justify-start text-left font-normal border-gray-200 focus:ring-1 focus:ring-green-500",
                              !newFacilityExpiryDate && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {newFacilityExpiryDate ? format(newFacilityExpiryDate, "PPP") : <span>Pick a date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={newFacilityExpiryDate}
                            onSelect={(date) => {
                              setNewFacilityExpiryDate(date);
                              setNewFacilityExpiryOpen(false);
                            }}
                            disabled={{ before: new Date() }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Additional Information Section */}
              <Card className="p-6 shadow-sm border-l-4 border-l-orange-600 bg-white">
                <div className="flex items-center gap-2 mb-6">
                  <div className="p-2 bg-orange-50 rounded-lg text-orange-600">
                    <FileText className="h-4 w-4" />
                  </div>
                  <h3 className="font-medium text-gray-900 text-sm italic">Additional Information</h3>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-gray-700">Notes</label>
                  <textarea
                    value={newFacilityNotes}
                    onChange={(e) => setNewFacilityNotes(e.target.value.slice(0, 200))}
                    maxLength={200}
                    rows={4}
                    className={cn(
                      "w-full p-3 rounded-md bg-white border focus:ring-1 outline-none transition-all resize-none text-sm",
                      newFacilityNotes.length >= 200
                        ? "border-red-300 focus:ring-red-500"
                        : "border-gray-200 focus:ring-green-500"
                    )}
                    placeholder="Enter notes here..."
                  />
                </div>
              </Card>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Credit Adjustment Sheet */}
      <Sheet open={adjustmentSheetOpen} onOpenChange={setAdjustmentSheetOpen}>
        <SheetContent side="right" className="sm:max-w-md">
          <SheetHeader className="border-b-0 pb-0">
            <SheetTitle className="text-2xl font-bold text-gray-900">
              {adjustmentType === "add" ? "Add credits" : "Subtract credits"}
            </SheetTitle>
          </SheetHeader>

          <div className="mt-2 mb-6">
            <p className="text-sm font-semibold text-gray-700">
              Service: <span className="font-normal text-gray-500">{selectedFacility?.facility_name}</span>
            </p>
          </div>

          <div className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">Amount <span className="text-red-500">*</span></label>
              <Input
                type="number"
                value={adjustmentAmount || ""}
                onChange={(e) => setAdjustmentAmount(Math.max(0, parseInt(e.target.value) || 0))}
                className={cn(
                  "h-11 bg-white border-gray-200 focus-visible:ring-1",
                  validationError ? "border-red-500 focus-visible:ring-red-500" : "focus-visible:ring-green-500"
                )}
              />
              {validationError && (
                <p className="text-xs text-red-500 mt-1 font-medium">{validationError}</p>
              )}
            </div>

            {adjustmentType === "add" && (
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">
                  Expiry Date <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <Popover open={adjustmentExpiryOpen} onOpenChange={setAdjustmentExpiryOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "h-11 w-full justify-start text-left font-normal border-gray-200 focus:ring-1 focus:ring-green-500",
                          !adjustmentExpiryDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {adjustmentExpiryDate ? format(adjustmentExpiryDate, "PPP") : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={adjustmentExpiryDate}
                        onSelect={(date) => {
                          setAdjustmentExpiryDate(date);
                          setAdjustmentExpiryOpen(false);
                        }}
                        disabled={{ before: new Date() }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="text-sm font-semibold text-gray-700">Notes</label>
                <span
                  className={cn(
                    "text-xs",
                    notes.length > 200 ? "text-red-500 font-semibold" : "text-gray-400"
                  )}
                >
                  {notes.length}/200
                </span>
              </div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, 200))}
                maxLength={200}
                rows={4}
                className={cn(
                  "w-full p-3 rounded-md bg-white border focus:ring-1 outline-none transition-all resize-none text-sm",
                  notes.length >= 200
                    ? "border-red-300 focus:ring-red-500"
                    : "border-gray-200 focus:ring-green-500"
                )}
                placeholder="Enter notes here..."
              />
            </div>

            <div className="pt-4 flex justify-end">
              <Button
                className="h-12 px-10 rounded-full bg-green-500 hover:bg-green-600 text-white font-semibold text-lg"
                disabled={
                  (adjustmentType === "add" ? addFacilityLoading : updateCreditsLoading) ||
                  !!validationError
                }
                onClick={handleSaveAdjustment}
              >
                {(adjustmentType === "add" ? addFacilityLoading : updateCreditsLoading) ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  adjustmentType === "add" ? "Add" : "Subtract"
                )}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Credit History Sheet */}
      <CreditHistorySheet
        open={historySheetOpen}
        onOpenChange={setHistorySheetOpen}
        facility={selectedFacility}
        orgId={orgId}
        memberId={memberId}
      />
    </>
  );
};

const EditorName = ({ id, type, orgId }: { id: number; type: string; orgId: number }) => {
  if (!type) return <span>-</span>;
  const isStaff = type === "staff";

  const { data: staffData, isLoading: isStaffLoading } = useGetStaffByIdQuery(id, {
    skip: !isStaff || !id || isNaN(id),
  });

  const { data: memberData, isLoading: isMemberLoading } = useGetMemberByIdQuery(
    { org_id: orgId, id: id },
    {
      skip: isStaff || !id || isNaN(id) || !orgId,
    }
  );

  if (isStaffLoading || isMemberLoading) {
    return <span className="text-gray-400">Loading...</span>;
  }

  if (isStaff) {
    if (!staffData?.first_name) return <span>-</span>;
    return (
      <span>
        {staffData.first_name} {staffData.last_name || ""} (Staff)
      </span>
    );
  }

  if (!memberData?.first_name) return <span>-</span>;
  return (
    <span>
      {memberData.first_name} {memberData.last_name || ""} (Member)
    </span>
  );
};

const CreditHistorySheet = ({
  open,
  onOpenChange,
  facility,
  orgId,
  memberId
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  facility: MembershipFacility | null;
  orgId: number;
  memberId: string | undefined;
}) => {
  const facilityId = (facility as any)?.facility_id || (facility as any)?.id;
  const { data: logsData, isLoading } = useGetCreditLogsQuery(
    {
      org_id: orgId,
      facility_id: facilityId,
      member_id: memberId,
      sort_order: "desc"
    },
    { skip: !open || !facility || !facilityId }
  );

  const timeline = useMemo(() => {
    if (!logsData?.data || !facilityId) return [];

    return logsData.data
      .filter((wallet: any) => wallet.facility_id === facilityId)
      .flatMap((wallet: any) =>
        (wallet.timeline ?? []).map((log: any) => ({
          ...log,
        }))
      )
  }, [logsData, facilityId]);

  const currentBalance =
    (logsData as any)?.current_balance ?? (facility as any)?.remaining_credits ?? 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-[80vw] overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-blue-600" />
            Credit History
          </SheetTitle>
          <p className="text-sm text-gray-500">
            Viewing logs for {facility?.facility_name}
          </p>
        </SheetHeader>
        {!isLoading && (
          <div className="mb-4 text-sm font-semibold text-gray-700">
            Current Balance:{" "}
            <span className="text-blue-600">{currentBalance}</span>
          </div>
        )}

        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <p className="text-sm text-gray-500">Loading history...</p>
          </div>
        ) : timeline.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <p>No history found for this facility.</p>
          </div>
        ) : (
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 hover:bg-gray-50">
                  <TableHead className="w-[100px]">Action</TableHead>
                  <TableHead>Changed</TableHead>
                  <TableHead>Balance</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Editor</TableHead>
                  <TableHead>Expiry</TableHead>
                  <TableHead className="text-right">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {timeline.map((log: any) => {
                  // const changeAmount =
                  //   (log.to_credits || 0) - (log.from_credits || 0);
                  const isUnlimited =
                  log.to_credits === "unlimited" || log.from_credits === "unlimited";

                  const changeAmount = isUnlimited
                    ? 0
                    : Number(log.to_credits || 0) - Number(log.from_credits || 0);
                  const action = String(log.action || "").toLowerCase();
                  const isExpired = action === "expired";
                  const isCredit = action === "credit" || (changeAmount > 0 && !isExpired);

                  return (
                    <TableRow key={log.id}>
                      <TableCell>
                        <span
                          className={cn(
                            "px-2 py-0.5 rounded text-[10px] font-bold uppercase",
                            isExpired
                              ? "bg-amber-100 text-amber-700"
                              : isCredit
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                          )}
                        >
                          {action}
                        </span>
                      </TableCell>
                      <TableCell
                        className={cn(
                          "font-semibold",
                          isExpired
                            ? "text-amber-600"
                            : isCredit
                              ? "text-green-600"
                              : "text-red-600"
                        )}
                      >
                        {isCredit ? "+" : ""}
                        {changeAmount}
                      </TableCell>
                      <TableCell className="font-medium">
                        {String(currentBalance).toLowerCase() === "unlimited"
                          ? "unlimited"
                          : log.to_credits}
                      </TableCell>
                      <TableCell className="max-w-[200px] text-gray-500 text-xs">
                        {log.message ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="cursor-pointer block truncate max-w-[200px]">
                                  {log.message}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-[300px] break-words bg-slate-900 text-white p-2 rounded shadow">
                                {log.message}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-gray-600">
                        {log.updated_by ? (
                          <EditorName
                            id={Number(log.updated_by)}
                            type={log.updated_by_type}
                            orgId={orgId}
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-gray-600">
                        {log.expiry && !isNaN(new Date(log.expiry).getTime())
                          ? format(new Date(log.expiry), "yyyy-MM-dd")
                          : "-"}
                      </TableCell>
                      <TableCell className="text-right text-xs text-gray-400">
                        {new Date(log.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default MemberMembershipFacilities;
